import React from "react";
import { connect } from "react-redux";
import { reduxForm, SubmissionError } from "redux-form";
import { Link, withRouter } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";

import _ from "underscore";

import Button from "../components/buttons/Button";
import InputText from "../components/InputText";
import InputSelect from "../components/InputSelect";
import InputCheckbox from "../components/InputCheckbox";

import { registration, init } from "../actions/registrationActions";
import UserService from "../services/userService";
import ErrorAlert from "../components/ErrorAlert";

/**
 * Checks if the given values in the form are valid.
 *
 * @param {Object}      values      The values of the written username and email.
 * @param {Object}      props       The props from the object with the class Registration.
 * @return {Object}                 An object with the errors.
 */
const validate = (values, props) => {
  const errors = {};

  var regEx = null;
  if (
    !_.isEmpty(values.password1) &&
    props.password.regex &&
    props.password.regexText
  ) {
    regEx = new RegExp(props.password.regex);
    errors.password1 = regEx.test(values.password1)
      ? undefined
      : props.password.regexText;
  }

  if (
    !_.isEmpty(values.password2) &&
    props.password.dontmatch &&
    (_.isEmpty(values.password1) || values.password1 !== values.password2)
  ) {
    errors.password2 = props.password.dontmatch;
  }

  return errors;
};

/**
 * Checks from the server if either the written username and email is unique and returns a resolved promise
 * if both are unique and otherwise a rejected promise with the value of what is not unique.
 *
 * @param {Object}      values      The values of the written username and email.
 * @param {Function}    dispatch    Redux's dispatch.
 * @param {Object}      props       The props from the object with the class Registration.
 * @return {Promise}                A rejected or resolved promised.
 */
const asyncValidate = (values, dispatch, props) => {
  return new Promise((resolve, reject) => {
    var usernameText = props.text("registration.username_exists");
    var emailText = props.text("registration.email_exists");
    var usernameExists =
      values.username !== null &&
      values.username !== undefined &&
      values.username !== "";
    var emailExists =
      values.email !== null &&
      values.email !== undefined &&
      values.email !== "";

    /* If both values has been entered then the server checks if they are unique and rejects those which are not */
    if (usernameExists === true && emailExists === true) {
      UserService.validateByKey(
        "username",
        values.username,
        props.server,
        dispatch
      )
        /* username is unique */
        .done(() => {
          UserService.validateByKey(
            "email",
            values.email,
            props.server,
            dispatch
          )
            /* both are unique */
            .done(() => {
              resolve();
            })
            /* email is not unique */
            .fail((xhr, ajaxOptions, thrownError) => {
              if (xhr.status !== 0) {
                reject({ email: emailText });
              } else {
                resolve(); //Error is shown in an alert and not on every field
              }
            });
        })
        /* username is not unique */
        .fail(() => {
          UserService.validateByKey(
            "email",
            values.email,
            props.server,
            dispatch
          )
            /* email is unique */
            .done(() => {
              reject({ username: usernameText });
            })
            /* email is not unique */
            .fail((xhr, ajaxOptions, thrownError) => {
              if (xhr.status !== 0) {
                reject({ username: usernameText, email: emailText });
              } else {
                resolve(); //Error is shown in an alert and not on every field
              }
            });
        });
    } else if (usernameExists === true) {
      /* If only username has been entered then the server checks if it is unique and rejects it if not */
      UserService.validateByKey(
        "username",
        values.username,
        props.server,
        dispatch
      )
        .done(() => {
          resolve();
        })
        .fail((xhr, ajaxOptions, thrownError) => {
          if (xhr.status !== 0) {
            reject({ username: usernameText });
          } else {
            resolve(); //Error is shown in an alert and not on every field
          }
        });
    } else if (emailExists === true) {
      /* If only email has been entered then the server checks if it is unique and rejects it if not */
      UserService.validateByKey("email", values.email, props.server, dispatch)
        .done(() => {
          resolve();
        })
        .fail((xhr, ajaxOptions, thrownError) => {
          if (xhr.status !== 0) {
            reject({ email: emailText });
          } else {
            resolve(); //Error is shown in an alert and not on every field
          }
        });
    } else {
      /* If username and email are not written then no errors exists */
      resolve();
    }
  });
};

class Registration extends React.Component {
  componentDidMount() {
    this.props.recaptchaInvalid();
    this.props.init(this.props.match.params.orgid);
  }

  componentWillUnmount() {
    this.props.clearRegistration();
  }

  onSubmit = values => {
    if (this.props.config.registration.license && values.license !== true) {
      throw new SubmissionError({
        license: this.props.text("registration.check_checkbox"),
      });
    }
    if (
      this.props.config.registration.recaptcha &&
      !this.props.recaptchavalid
    ) {
      return;
    }
    this.props.registration(values);
  };

  password = field => {
    if (!field.visible) {
      return "";
    }
    return (
      <div>
        <InputText
          name="password1"
          text={this.props.text("common.password1")}
          field={field}
          autocomplete={false}
        />
        <InputText
          name="password2"
          text={this.props.text("common.password2")}
          field={field}
          autocomplete={false}
        />
      </div>
    );
  };

  organisation = (organisations, texts) => {
    if (!Array.isArray(organisations)) {
      return "";
    }
    return (
      <div>
        <InputSelect
          name="orgID"
          text={this.props.text("common.organisation")}
          texts={texts}
          field={{ organisations: organisations, required: true }}
        />
      </div>
    );
  };

  extraInfo = info => {
    let ret = "";

    return ret;
  };

  render() {
    if (this.props.loading) {
      return <div></div>;
    }

    if (!this.props.enabled) {
      return <div>{this.props.text("registration.not_enabled")}</div>;
    }

    if (this.props.registrationComplete) {
      if (this.props.router.location.search.indexOf("hideLogin=true") !== -1) {
        return (
          <main>
            <h1>{this.props.text("registration.completed_headline")}</h1>
            <p>{this.props.text("registration.completed")}</p>
          </main>
        );
      } else {
        return (
          <main>
            <h1>{this.props.text("registration.completed_headline")}</h1>
            <p>{this.props.text("registration.completed")}</p>
            <Link className="underlined" to={"/"}>
              {this.props.text("common.to_startpage")}
            </Link>
          </main>
        );
      }
    }

    var errorText = "";
    if (this.props.serverError === true) {
      errorText = this.props.text("network.networkError");
    } else if (this.props.registrationError === true) {
      errorText =
        this.props.text("registration.error") +
        " (" +
        this.props.registrationErrorText +
        ")";
    }

    return (
      <main>
        {!this.props.registrationInProgress && (
          <div className="row">
            <h1 id="h1id">{this.props.text("registration.title")}</h1>

            <div
              className={
                this.props.text("registration.info") !== " "
                  ? "col-md-8 col-lg-9 col-xl-9"
                  : ""
              }
            >
              {errorText !== "" && <ErrorAlert text={errorText} />}

              <form onSubmit={this.props.handleSubmit(this.onSubmit)}>
                <p>{this.props.text("common.required")}</p>

                {this.organisation(
                  this.props.config.registration.organisations,
                  this.props.text
                )}

                <InputText
                  name="username"
                  text={this.props.text("common.username")}
                  field={this.props.username}
                  autocomplete={false}
                  autoCapitalize={"none"}
                />

                {this.password(this.props.password)}

                <InputText
                  name="firstname"
                  text={this.props.text("common.firstname")}
                  field={this.props.firstname}
                  autocomplete={false}
                />
                <InputText
                  name="lastname"
                  text={this.props.text("common.lastname")}
                  field={this.props.lastname}
                  autocomplete={false}
                />
                <InputText
                  name="email"
                  text={this.props.text("common.email")}
                  field={this.props.email}
                  autocomplete={false}
                />
                <InputText
                  name="address"
                  text={this.props.text("common.address")}
                  field={this.props.address}
                  autocomplete={false}
                />
                <InputText
                  name="postcode"
                  text={this.props.text("common.zipcode")}
                  field={this.props.postcode}
                  autocomplete={false}
                />
                <InputText
                  name="city"
                  text={this.props.text("common.city")}
                  field={this.props.city}
                  autocomplete={false}
                />
                <InputText
                  name="phonenumber"
                  text={this.props.text("common.phone")}
                  field={this.props.phonenumber}
                  autocomplete={false}
                />
                <InputText
                  name="mobilenumber"
                  text={this.props.text("common.mobile")}
                  field={this.props.mobilenumber}
                  autocomplete={false}
                />
                <InputText
                  name="isdnnumber"
                  text={this.props.text("common.isdn")}
                  field={this.props.isdnnumber}
                  autocomplete={false}
                />
                <InputText
                  name="sipaddress"
                  text={this.props.text("common.sip")}
                  field={this.props.sipaddress}
                  autocomplete={false}
                />
                <InputText
                  name="gggnumber"
                  text={this.props.text("common.3gnumber")}
                  field={this.props.gggnumber}
                  autocomplete={false}
                />
                <InputText
                  name="faxnumber"
                  text={this.props.text("common.fax")}
                  field={this.props.faxnumber}
                  autocomplete={false}
                />

                {this.props.config.registration.license && (
                  <InputCheckbox
                    name="license"
                    text={this.props.text}
                    field={{ visible: true }}
                    autocomplete={false}
                    type={"checkbox"}
                  />
                )}

                {this.props.config.registration.recaptcha && (
                  <div>
                    <ReCAPTCHA
                      sitekey={this.props.config.recaptchakey}
                      onChange={this.props.recaptchaValid}
                    />
                    <br />
                  </div>
                )}

                <Button type="submit" text={this.props.text("common.send")} />
              </form>
            </div>
            {this.props.text("registration.info") !== " " && (
              <div className="col">
                {this.props
                  .text("registration.info")
                  .split("\n")
                  .map(function(element) {
                    return (
                      <p aria-label={this.props.text("aria.accept_terms")}>
                        {element}
                      </p>
                    );
                  })}
              </div>
            )}
          </div>
        )}
      </main>
    );
  }
}

const mapStateToProps = state => {
  return {
    router: state.router,
    config: state.config,
    registrationInProgress: state.registration.registrationInProgress,
    registrationComplete: state.registration.registrationComplete,
    registrationError: state.registration.registrationError,
    registrationErrorText: state.registration.registrationErrorText,
    serverError: state.network.serverError,
    text: str => state.config.text(str),
    server: state.config.server,
    loading: state.config.text == null ? true : false,
    recaptchavalid: state.registration.recaptchavalid,
    ...state.registration,
    enabled: state.config.mode.registration_enabled,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    recaptchaValid: () => {
      dispatch({ type: "REGISTRATION_RECAPTCHA_VALID" });
    },
    recaptchaInvalid: () => {
      dispatch({ type: "REGISTRATION_RECAPTCHA_INVALID" });
    },
    clearRegistration: () => {
      dispatch({ type: "REGISTRATION_CLEAR" });
    },
    registration: values => {
      dispatch(registration(values));
    },
    init: orgid => {
      dispatch(init(orgid));
    },
  };
};

Registration = reduxForm({
  form: "registration",
  validate,
  asyncValidate,
  asyncBlurFields: ["username", "email"],
})(Registration);

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(Registration)
);






// WEBPACK FOOTER //
// ./src/containers/Registration.js